import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Search, RefreshCw, Download, Loader2, AlertCircle, Briefcase, Users, UserX, GitBranch
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ReportPeriodFilter from './ReportPeriodFilter';
import {
  ALL, NO_JOB, NO_STAGE, JOB_STATUS_COLORS, STICKY_COL, jobStatusRank, isPlainLeftClick, formatDate,
  fileSafe, downloadWorkbook, hasPeriod, periodBounds, periodPhrase, periodFileTag
} from './reportUtils';

// Passed through: what a period selects. Stage moves made in it by default.
const BASIS_OPTIONS = [
  { value: 'moved', label: 'Moved stage in period' },
  { value: 'added', label: 'Added in period' }
];

const byStatusThenNewest = (a, b) => (
  jobStatusRank(a.status) - jobStatusRank(b.status)
  || (b.created_at || '').localeCompare(a.created_at || '')
);

const stageLabel = (stage) => stage || 'No stage';

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

// A job row's per-stage counts for the report mode.
const stageCountsOf = (job, passed) => (passed ? job.reached_counts : job.stage_counts);

// Whole-number share of `total`.
const pct = (count, total) => (total > 0 ? Math.round((count / total) * 100) : 0);

const StatTile = ({ label, value, detail, icon: Icon }) => (
  <Card>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground mb-1">{label}</p>
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
          {detail && <div className="text-xs text-muted-foreground mt-1">{detail}</div>}
        </div>
        <Icon className="w-7 h-7 text-slate-400 shrink-0" aria-hidden="true" />
      </div>
    </CardContent>
  </Card>
);

// Every job against the stages of its pipeline, in one of two modes:
//   'current' (the main report): how many candidates are at each stage now;
//   'passed': how many have passed through each stage at any point, with
//     their share of the job's candidates.
// A period narrows 'current' to candidates added in it; 'passed' then counts
// either the stage moves made in it or the candidates added in it (`basis`).
// Jobs are grouped by pipeline template, since each template has its own
// stages. Every count links to the candidate report listing exactly the
// candidates it counts.
const StageSummaryReport = ({
  mode = 'current', period = { from: ALL, to: ALL }, basis = ALL, onPeriodChange, onBasisChange,
  getReportUrl, onOpenReport
}) => {
  const { getStageSummaryReport, getStageFunnelReport } = useRecruitment();
  const passed = mode === 'passed';
  const fetchReport = passed ? getStageFunnelReport : getStageSummaryReport;
  const { from, to } = period;
  const periodSet = hasPeriod(from, to);
  const listBasis = passed && basis !== 'added' ? 'moved' : 'added';
  // Moves made in a period are not a share of anything (October's offers can
  // come from September's applicants), so those counts go without a %.
  const movedCounts = passed && periodSet && listBasis === 'moved';
  const showShare = passed && !movedCounts;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const latestRequest = useRef(0);

  const loadReport = useCallback(async () => {
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    setLoading(true);
    setError(null);
    const result = await fetchReport({ ...periodBounds(from, to), basis: passed && periodSet ? listBasis : null });
    // Another period was asked for meanwhile; only its answer may show.
    if (request !== latestRequest.current) return;
    if (result.success) {
      setReport(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  }, [fetchReport, from, to, passed, periodSet, listBasis]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const { visibleJobs, hiddenJobs } = useMemo(() => {
    if (!report) return { visibleJobs: [], hiddenJobs: 0 };
    const q = search.trim().toLowerCase();
    const matching = report.jobs.filter((job) => {
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      const matchesSearch = !q || [job.title, job.department, job.location]
        .some((field) => field?.toLowerCase().includes(q));
      return matchesStatus && matchesSearch;
    });
    // In a period, a job with nothing in it would only add a row of zeros.
    const visible = periodSet ? matching.filter((job) => job.total > 0) : matching;
    return { visibleJobs: visible, hiddenJobs: matching.length - visible.length };
  }, [report, search, statusFilter, periodSet]);

  // One group per pipeline template that has a visible job: the default
  // pipeline first, then the rest in their saved order.
  const groups = useMemo(() => {
    if (!report) return [];
    const pipelines = [...report.pipelines].sort((a, b) => Number(b.is_default) - Number(a.is_default));
    const byPipeline = new Map(pipelines.map((p) => [p.id, { pipeline: p, jobs: [] }]));
    visibleJobs.forEach((job) => {
      if (!byPipeline.has(job.pipeline_id)) {
        // Only when no pipeline template exists at all.
        byPipeline.set(job.pipeline_id, {
          pipeline: { id: job.pipeline_id, name: 'No pipeline', stages: Object.keys(stageCountsOf(job, passed)), is_default: false },
          jobs: []
        });
      }
      byPipeline.get(job.pipeline_id).jobs.push(job);
    });
    return [...byPipeline.values()]
      .filter((group) => group.jobs.length > 0)
      .map(({ pipeline, jobs }) => {
        const stages = pipeline.stages || [];
        const stageTotals = Object.fromEntries(
          stages.map((stage) => [stage, jobs.reduce((sum, job) => sum + (stageCountsOf(job, passed)[stage] || 0), 0)])
        );
        return {
          key: pipeline.id ?? 'none',
          name: pipeline.name,
          isDefault: pipeline.is_default,
          stages,
          jobs: [...jobs].sort(byStatusThenNewest),
          stageTotals,
          // Only the current-stage report has stages outside the pipeline.
          otherTotal: jobs.reduce((sum, job) => sum + (job.other_count || 0), 0),
          total: jobs.reduce((sum, job) => sum + job.total, 0)
        };
      });
  }, [report, visibleJobs, passed]);

  const totals = useMemo(() => {
    const byStatus = {};
    visibleJobs.forEach((job) => { byStatus[job.status] = (byStatus[job.status] || 0) + 1; });
    return {
      jobs: visibleJobs.length,
      candidates: visibleJobs.reduce((sum, job) => sum + job.total, 0),
      byStatus
    };
  }, [visibleJobs]);

  const jobsDetail = ['Open', 'On Hold', 'Closed']
    .filter((status) => totals.byStatus[status])
    .map((status) => `${totals.byStatus[status]} ${status.toLowerCase()}`)
    .join(' · ');

  // What the numbers mean for the chosen period, said in words.
  const phrase = periodPhrase(from, to);
  let periodDescription = null;
  if (periodSet && !passed) periodDescription = `Candidates added ${phrase}, counted at the stage they are at now.`;
  else if (movedCounts) periodDescription = `Moves into each stage made ${phrase}, whenever the candidate was added.`;
  else if (periodSet) periodDescription = `Candidates added ${phrase}, and every stage they have reached since.`;
  const hiddenNote = hiddenJobs > 0
    ? ` ${plural(hiddenJobs, 'job')} with nothing in this period ${hiddenJobs === 1 ? 'is' : 'are'} hidden.`
    : '';

  const candidatesTile = !periodSet
    ? { label: 'Candidates in these jobs', detail: passed ? 'Each counted at every stage they have been at' : 'Counted at their current stage' }
    : movedCounts
      ? { label: 'Candidates who moved', detail: 'Into at least one stage in this period' }
      : { label: 'Candidates added', detail: passed ? 'Each counted at every stage they have reached' : 'Counted at their current stage' };

  const openLink = (filters) => (e) => {
    if (!isPlainLeftClick(e)) return;
    e.preventDefault();
    onOpenReport(filters);
  };

  // A count's link: the candidate report for the same period and basis, so it
  // lists exactly the candidates that were counted. Current stage leaves the
  // basis unset: the candidate report's default (added) is already its basis,
  // and a set basis would follow into Passed through and override its default.
  const candidateLink = (filters) => ({
    view: 'candidates', from, to, basis: passed && periodSet ? listBasis : ALL, ...filters
  });

  // An "Other" count leads straight to that stage when there is only one
  // off-pipeline stage; otherwise to the whole job, where the candidate
  // report lists each stage separately.
  const otherFilters = (job) => (
    job.other_stages.length === 1
      ? candidateLink({ job: job.id, stage: job.other_stages[0].stage || NO_STAGE })
      : candidateLink({ job: job.id })
  );

  const countCell = (key, count, filters, label) => (
    <TableCell key={key} className="text-center tabular-nums px-3">
      {count > 0 ? (
        <a
          href={getReportUrl(filters)}
          onClick={openLink(filters)}
          className="inline-flex min-w-[2rem] justify-center rounded-md px-2 py-1 font-semibold text-purple-700 hover:bg-purple-50 hover:underline"
          aria-label={label}
        >
          {count}
        </a>
      ) : (
        <span className="text-slate-300">0</span>
      )}
    </TableCell>
  );

  const shareCell = (key, count, total, filters, label) => {
    const share = pct(count, total);
    return (
      <TableCell key={key} className="text-center px-3">
        {count > 0 ? (
          <a
            href={getReportUrl(filters)}
            onClick={openLink(filters)}
            className="group/cell inline-flex flex-col items-center gap-1 rounded-md px-2 py-1 hover:bg-purple-50"
            title={`${count} of ${total} candidates (${share}%)`}
            aria-label={`${count} of ${total} candidates (${share}%) ${label}`}
          >
            <span className="tabular-nums whitespace-nowrap">
              <span className="font-semibold text-purple-700 group-hover/cell:underline">{count}</span>
              <span className="ml-1 text-xs text-muted-foreground">{share}%</span>
            </span>
            {/* Share of the job's candidates; never narrower than a sliver, so small shares still show. */}
            <span className="block h-1 w-14 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
              <span className="block h-full rounded-full bg-purple-500" style={{ width: `${Math.max(share, 4)}%` }} />
            </span>
          </a>
        ) : (
          <span className="text-slate-300">0</span>
        )}
      </TableCell>
    );
  };

  const stageCell = (job, stage) => {
    if (!passed) {
      const count = job.stage_counts[stage] || 0;
      return countCell(stage, count, candidateLink({ job: job.id, stage }), `${plural(count, 'candidate')} at ${stage} for ${job.title}`);
    }
    const count = job.reached_counts[stage] || 0;
    const filters = candidateLink({ job: job.id, reached: stage });
    return showShare
      ? shareCell(stage, count, job.total, filters, `passed through ${stage} for ${job.title}`)
      : countCell(stage, count, filters, `${plural(count, 'candidate')} moved into ${stage} in this period for ${job.title}`);
  };

  const jobColumns = (job) => [job.title, job.department || '', job.location || '', job.status || ''];

  // Current stage: a count per stage, then Other and Total.
  const currentSheet = (group) => {
    const hasOther = group.otherTotal > 0;
    const header = ['Job', 'Department', 'Location', 'Job Status', ...group.stages, ...(hasOther ? ['Other Stages'] : []), 'Total'];
    const rows = group.jobs.map((job) => [
      ...jobColumns(job),
      ...group.stages.map((stage) => job.stage_counts[stage] || 0),
      ...(hasOther ? [job.other_count] : []),
      job.total
    ]);
    const totalRow = ['Total', '', '', '', ...group.stages.map((stage) => group.stageTotals[stage]), ...(hasOther ? [group.otherTotal] : []), group.total];
    return {
      rows: [header, ...rows, totalRow],
      widths: [32, 18, 16, 12, ...group.stages.map((stage) => Math.max(10, stage.length + 2)), ...(hasOther ? [13] : []), 8]
    };
  };

  // Passed through: the job's candidates, then a count (and a %) per stage.
  const passedSheet = (group) => {
    const perStage = (count, total) => (showShare ? [count, pct(count, total)] : [count]);
    const header = ['Job', 'Department', 'Location', 'Job Status', 'Candidates', ...group.stages.flatMap((stage) => (showShare ? [stage, `${stage} %`] : [stage]))];
    const rows = group.jobs.map((job) => [
      ...jobColumns(job),
      job.total,
      ...group.stages.flatMap((stage) => perStage(job.reached_counts[stage] || 0, job.total))
    ]);
    const totalRow = ['Total', '', '', '', group.total, ...group.stages.flatMap((stage) => perStage(group.stageTotals[stage], group.total))];
    return {
      rows: [header, ...rows, totalRow],
      widths: [32, 18, 16, 12, 11, ...group.stages.flatMap((stage) => (
        showShare ? [Math.max(10, stage.length + 2), Math.max(8, stage.length + 4)] : [Math.max(10, stage.length + 2)]
      ))]
    };
  };

  const handleExport = () => {
    if (groups.length === 0) {
      toast.error('There is nothing to export');
      return;
    }
    const sheets = groups.map((group) => ({
      name: group.name,
      note: periodDescription,
      ...(passed ? passedSheet(group) : currentSheet(group))
    }));
    const fileName = [
      passed ? 'Recruitment_Stages_Passed_Through' : 'Recruitment_Stage_Summary',
      passed && periodSet ? (movedCounts ? 'Moved' : 'Added') : null,
      statusFilter === 'all' ? null : `${fileSafe(statusFilter)}_Jobs`,
      periodFileTag(from, to) || null,
      format(new Date(), 'yyyy-MM-dd')
    ].filter(Boolean).join('_');
    downloadWorkbook(sheets, `${fileName}.xlsx`);
    toast.success('Report exported');
  };

  if (loading && !report) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error && !report) {
    return (
      <Card>
        <CardContent className="py-12 flex flex-col items-center gap-3 text-center">
          <AlertCircle className="w-10 h-10 text-red-400" />
          <p className="text-slate-700 font-medium">Couldn't load the report</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" onClick={loadReport}>
            <RefreshCw className="w-4 h-4 mr-2" /> Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid={passed ? 'stage-funnel-report' : 'stage-summary-report'}>
      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          <ReportPeriodFilter
            from={from}
            to={to}
            onChange={onPeriodChange}
            basis={listBasis}
            basisOptions={passed ? BASIS_OPTIONS : null}
            onBasisChange={onBasisChange}
            description={periodDescription && `${periodDescription}${hiddenNote}`}
          />
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search jobs by title, department or location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                aria-label="Search jobs"
                data-testid="report-job-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" aria-label="Job status" data-testid="report-job-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All job statuses</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 ml-auto">
              {report?.generated_at && (
                <span className="hidden lg:inline text-xs text-muted-foreground">
                  Updated {formatDate(report.generated_at, 'dd MMM, hh:mm a')}
                </span>
              )}
              <Button variant="outline" size="icon" onClick={loadReport} disabled={loading} aria-label="Refresh report" data-testid="refresh-stage-summary">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" onClick={handleExport} disabled={groups.length === 0 || loading} data-testid="export-stage-summary">
                <Download className="w-4 h-4 mr-2" /> Export to Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Couldn't refresh the report: {error}. Showing the last loaded figures.
        </div>
      )}

      {/* Dimmed while a new period loads, so old figures never pass for new ones. */}
      <div className={`flex flex-col gap-6 transition-opacity ${loading ? 'opacity-50' : ''}`} aria-busy={loading}>
        {/* Headline numbers */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatTile label="Jobs" value={totals.jobs} detail={jobsDetail || null} icon={Briefcase} />
          <StatTile label={candidatesTile.label} value={totals.candidates} detail={candidatesTile.detail} icon={Users} />
          <StatTile
            label="Not linked to a job"
            value={report.unassigned.total}
            icon={UserX}
            detail={report.unassigned.total > 0 ? (
              <a
                href={getReportUrl(candidateLink({ job: NO_JOB }))}
                onClick={openLink(candidateLink({ job: NO_JOB }))}
                className="text-purple-600 hover:underline"
                data-testid="report-unassigned-link"
              >
                View these candidates
              </a>
            ) : (periodSet ? 'None in this period' : 'Every candidate has a job')}
          />
        </div>

        {/* One table per pipeline */}
        {report.jobs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No jobs yet. Jobs you create in the Jobs tab will appear here.
            </CardContent>
          </Card>
        ) : groups.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              {periodSet && hiddenJobs > 0 ? 'Nothing happened in these jobs in this period.' : 'No jobs match these filters.'}
            </CardContent>
          </Card>
        ) : (
          groups.map((group) => {
            const hasOther = group.otherTotal > 0;
            return (
              <Card key={group.key} data-testid={`stage-summary-group-${group.key}`}>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <GitBranch className="w-4 h-4 text-purple-600" aria-hidden="true" />
                      <CardTitle className="text-base">{group.name}</CardTitle>
                      {group.isDefault && <Badge variant="outline" className="font-normal">Default pipeline</Badge>}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {plural(group.jobs.length, 'job')} · {plural(group.total, 'candidate')}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead className={`${STICKY_COL} bg-white min-w-[150px] sm:min-w-[220px]`}>Job</TableHead>
                          {group.stages.map((stage) => (
                            <TableHead key={stage} className="text-center whitespace-nowrap px-3">{stage}</TableHead>
                          ))}
                          {hasOther && (
                            <TableHead className="text-center whitespace-nowrap px-3" title="Candidates whose stage is not part of this pipeline, e.g. a stage that was later renamed or removed">
                              Other
                            </TableHead>
                          )}
                          <TableHead
                            className="text-center px-3 text-slate-900"
                            title={movedCounts ? 'Candidates who moved into at least one stage in this period' : undefined}
                          >
                            {passed ? 'Candidates' : 'Total'}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.jobs.map((job) => (
                          <TableRow key={job.id} className="group hover:bg-slate-50" data-testid={`stage-summary-row-${job.id}`}>
                            <TableCell className={`${STICKY_COL} bg-white group-hover:bg-slate-50`}>
                              <div className="flex items-center gap-2">
                                <a
                                  href={getReportUrl(candidateLink({ job: job.id }))}
                                  onClick={openLink(candidateLink({ job: job.id }))}
                                  className="font-medium text-slate-900 hover:text-purple-600 hover:underline"
                                >
                                  {job.title}
                                </a>
                                <Badge className={`${JOB_STATUS_COLORS[job.status] || 'bg-slate-100 text-slate-700'} font-normal shrink-0`}>
                                  {job.status}
                                </Badge>
                              </div>
                              {(job.department || job.location) && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {[job.department, job.location].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </TableCell>
                            {group.stages.map((stage) => stageCell(job, stage))}
                            {hasOther && (
                              <TableCell className="text-center tabular-nums px-3">
                                {job.other_count > 0 ? (
                                  <a
                                    href={getReportUrl(otherFilters(job))}
                                    onClick={openLink(otherFilters(job))}
                                    className="inline-flex min-w-[2rem] justify-center rounded-md px-2 py-1 font-semibold text-amber-700 hover:bg-amber-50 hover:underline"
                                    title={job.other_stages.map((o) => `${stageLabel(o.stage)}: ${o.count}`).join('\n')}
                                    aria-label={`${plural(job.other_count, 'candidate')} at stages outside this pipeline for ${job.title}`}
                                  >
                                    {job.other_count}
                                  </a>
                                ) : (
                                  <span className="text-slate-300">0</span>
                                )}
                              </TableCell>
                            )}
                            <TableCell className="text-center tabular-nums px-3">
                              {job.total > 0 ? (
                                <a
                                  href={getReportUrl(candidateLink({ job: job.id }))}
                                  onClick={openLink(candidateLink({ job: job.id }))}
                                  className="inline-flex min-w-[2rem] justify-center rounded-md px-2 py-1 font-bold text-slate-900 hover:bg-slate-100 hover:underline"
                                  aria-label={movedCounts
                                    ? `${plural(job.total, 'candidate')} who moved in this period for ${job.title}`
                                    : `All ${plural(job.total, 'candidate')} for ${job.title}`}
                                >
                                  {job.total}
                                </a>
                              ) : (
                                <span className="text-slate-300 font-semibold">0</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      {group.jobs.length > 1 && (
                        <TableFooter className="bg-slate-50">
                          <TableRow className="hover:bg-slate-50">
                            <TableCell className={`${STICKY_COL} bg-slate-50 font-semibold text-slate-900`}>Total</TableCell>
                            {group.stages.map((stage) => (
                              <TableCell key={stage} className="text-center tabular-nums whitespace-nowrap px-3 font-semibold text-slate-700">
                                {group.stageTotals[stage]}
                                {showShare && (
                                  <span className="ml-1 text-xs font-normal text-muted-foreground">{pct(group.stageTotals[stage], group.total)}%</span>
                                )}
                              </TableCell>
                            ))}
                            {hasOther && (
                              <TableCell className="text-center tabular-nums px-3 font-semibold text-slate-700">{group.otherTotal}</TableCell>
                            )}
                            <TableCell className="text-center tabular-nums px-3 font-bold text-slate-900">{group.total}</TableCell>
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StageSummaryReport;
